/**
 * MODULE: _shared/frontend-shell/src/layout/shellAssistant/shellAssistantDockContext.ts
 * PURPOSE: Live dock page context, module routing, and shell_context payloads [2.2.1345].
 * PUBLIC_API: mergeLiveDockPageContext, inferDockIdleContextQueryType, buildDockShellContext,
 *              dockIdleComposerPlaceholder, collectDockIdleStarterChips, resolveDockIdleChipPromptAtSend
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-06-22 [2.2.1470] — Manager dashboard idle dock chips on / when sales persona is manager.
 * RECENT_PRIOR: 2026-06-20 [2.2.1457] — Team quotas dock routing; settings `tab=sales-targets` starter chips.
 * RECENT_PRIOR: 2026-06-08 [2.2.1351] — Context-aware idle dock placeholder + compact starter chips.
 */

import {
  inferShellModuleFromPath,
  mergeFocusEntityHintFromShellRoute,
  type RouteFocusEntityHint,
} from '@shared/lib/frontend/utils/assistantRouteFocusHint';
import type {
  AssistantContextAction,
  AssistantContextResponse,
  AssistantShellContext,
} from '@shared/lib/frontend/api/assistantContextApi';
import {
  isAdvisorExecuteAction,
  isComposeDraftAction,
  isNavigateAssistantAction,
  isOpenEmailTemplateCreateAction,
  isOpenSalesTargetSetAction,
  isPromptAssistantAction,
} from '@shared/lib/frontend/utils/assistantContextActionHandlers';
import { collectProactiveAdvisorContextActions } from '../../components/assistant/assistantProactiveAdvisorUtils';
import type { AssistantDockRouteContext } from './ShellAssistantContext';
import { readDashboardSalesPersona } from '../../pages/dashboard/dashboardRoleViews';

export type DockPageContextInput = {
  path: string;
  title?: string;
};

export type DockWidgetContextInput = {
  contextWidgetId?: string | null;
  widgetTitle?: string | null;
};

export type DockIdleStarterChip = {
  label: string;
  prompt: string;
  /** When set, panel routes the click through structured context actions (compose/navigate). */
  contextAction?: AssistantContextAction;
};

/** Parse deal display name from shell page title (e.g. "Contoso … — Opportunities — Brazn"). */
export function dealDisplayNameFromPageTitle(pageTitle?: string | null): string {
  const title = (pageTitle ?? '').trim();
  if (!title) return '';
  let head = title;
  for (const sep of [' — ', ' - ']) {
    if (head.includes(sep)) {
      head = head.split(sep)[0].trim();
      break;
    }
  }
  if (!head) return '';
  const lower = head.toLowerCase();
  if (
    lower === 'brazn' ||
    lower === 'brazn platform' ||
    lower === 'deals' ||
    lower === 'crm' ||
    lower === 'opportunities'
  ) {
    return '';
  }
  return head;
}

/** Re-resolve chip prompt at send time so stale brain focus names are not baked in. */
export function resolveDockIdleChipPromptAtSend(
  chip: DockIdleStarterChip,
  input: {
    pagePath?: string;
    pageTitle?: string | null;
    focusEntityName?: string | null;
  },
): string {
  const path = (input.pagePath ?? '').split('?')[0].trim().toLowerCase();
  if (!path.match(/^\/crm\/deals\/[0-9a-f-]{36}$/i)) {
    return chip.prompt;
  }
  const dealName = resolveDealFocusDisplayName(input, true);
  const label = chip.label.trim();
  if (/^run\s+.+\s+playbook$/i.test(label)) {
    return dealFocusCoachingPromptForShell(
      label,
      { action: 'deal_sop_coaching', prompt_text: chip.prompt },
      dealName,
    );
  }
  if (/next step/i.test(label)) {
    return dealFocusCoachingPromptForShell(
      label,
      { action: 'deal_next_step', prompt_text: chip.prompt },
      dealName,
    );
  }
  if (/objection/i.test(label)) {
    return dealFocusCoachingPromptForShell(
      label,
      { action: 'deal_objection_coaching', prompt_text: chip.prompt },
      dealName,
    );
  }
  return chip.prompt;
}

/** Prefer live shell deal title over stale brain prompt_text on deal detail pages. */
export function dealFocusCoachingPromptForShell(
  label: string,
  action: Pick<AssistantContextAction, 'action' | 'prompt_text'>,
  dealName: string,
): string {
  const name = (dealName || '').trim() || 'this deal';
  const kind = (action.action || '').trim();
  const stageMatch = label.match(/^Run\s+(.+?)\s+playbook$/i);
  if (kind === 'deal_sop_coaching' || stageMatch) {
    const stage = (stageMatch?.[1] || 'Discovery').trim();
    return `How should I run a ${stage} call for ${name} using our sales playbook?`;
  }
  if (kind === 'deal_next_step' || /next step/i.test(label)) {
    return `What's the next step on ${name}?`;
  }
  if (kind === 'deal_objection_coaching' || /objection/i.test(label)) {
    return `How do I handle price objections on ${name} from our sales playbook?`;
  }
  return (action.prompt_text || label || '').trim();
}

function resolveDealFocusDisplayName(
  input: {
    focusEntityName?: string | null;
    pageTitle?: string | null;
  },
  dealDetailPath: boolean,
): string {
  const fromEntity = (input.focusEntityName ?? '').trim();
  if (fromEntity && !dealDetailPath) return fromEntity;
  if (dealDetailPath) {
    return dealDisplayNameFromPageTitle(input.pageTitle) || fromEntity || 'this deal';
  }
  return '';
}

/** Always prefer the live router path/title while the dock is open. */
export function mergeLiveDockPageContext(
  live: DockPageContextInput,
  sessionSnapshot?: DockPageContextInput | null
): DockPageContextInput {
  const path = (live.path || sessionSnapshot?.path || '').trim();
  const title = (live.title || sessionSnapshot?.title || '').trim();
  return {
    path,
    ...(title ? { title } : {}),
  };
}

/**
 * Idle dock: pick a better default than general_chat before the user sends a message.
 * After routing metadata or user text exists, callers should use stream/topic query types.
 */
export function inferDockIdleContextQueryType(
  route: string,
  widgetContext?: DockWidgetContextInput | null
): string {
  const widgetId = (widgetContext?.contextWidgetId ?? '').trim();
  if (widgetId) return 'widget';

  const fullRoute = route.trim().toLowerCase();
  const path = (fullRoute.split('?')[0] ?? fullRoute).trim();

  if (path.includes('/crm/deals') || path === '/crm') {
    const dealDetail = /^\/crm\/deals\/[0-9a-f-]{36}$/i.test(path);
    return dealDetail ? 'crud_read' : 'analyze_pipeline';
  }
  if (path.includes('/crm/my-quota') || path.endsWith('/my-quota')) return 'get_quota_attainment';
  if (
    path.includes('/settings') &&
    (fullRoute.includes('tab=territories') || fullRoute.includes('territories'))
  ) {
    return 'get_territory_quota_alignment';
  }

  if (
    path.includes('/settings') &&
    (fullRoute.includes('tab=sales-targets') || fullRoute.includes('sales-targets'))
  ) {
    return 'get_team_quota_rollup';
  }
  if (path.includes('/crm/contacts')) return 'general_chat';
  if (path.includes('/calendar')) return 'general_chat';
  if (path.includes('/messaging')) return 'team-messaging';
  if (path.includes('/pages') || path.includes('/workspaces/')) return 'widget';
  if (path.includes('/activities') || path.includes('/activity')) return 'general_chat';
  if (path.includes('/sales-hub')) return 'general_chat';
  if (path === '/langflow-assistant' || path === '/assistant' || path === '/home' || path === '/') {
    return 'analyze_pipeline';
  }

  return 'general_chat';
}

export function buildDockFocusEntityHint(
  routeMeta: AssistantDockRouteContext
): RouteFocusEntityHint | null {
  return mergeFocusEntityHintFromShellRoute({
    route: routeMeta.route,
    selectedRecord: routeMeta.selectedRecord,
  });
}

export function buildDockShellContext(
  pageContext: DockPageContextInput,
  routeMeta: AssistantDockRouteContext,
  widgetContext?: DockWidgetContextInput | null,
  overlayContext?: {
    active_modal?: string | null;
    active_modal_title?: string | null;
    active_modal_context?: Record<string, unknown> | null;
  } | null
): AssistantShellContext {
  const path = (pageContext.path || routeMeta.route || '').trim();
  const title = (pageContext.title ?? '').trim();
  const widgetId = (widgetContext?.contextWidgetId ?? '').trim();
  const widgetTitle = (widgetContext?.widgetTitle ?? '').trim();
  return {
    page_path: path,
    ...(title ? { page_title: title } : {}),
    module: inferShellModuleFromPath(path),
    selected_record_id: routeMeta.selectedRecord ?? null,
    ...(routeMeta.activeFilters && Object.keys(routeMeta.activeFilters).length > 0
      ? { active_filters: routeMeta.activeFilters }
      : {}),
    ...(widgetId ? { active_widget_id: widgetId } : {}),
    ...(widgetTitle ? { active_widget_title: widgetTitle } : {}),
    ...(overlayContext?.active_modal
      ? {
          active_modal: overlayContext.active_modal,
          ...(overlayContext.active_modal_title
            ? { active_modal_title: overlayContext.active_modal_title }
            : {}),
          ...(overlayContext.active_modal_context
            ? { active_modal_context: overlayContext.active_modal_context }
            : {}),
        }
      : {}),
  };
}

/** Context-aware composer hint when the dock is open with no messages yet [2.2.1351]. */
export function dockIdleComposerPlaceholder(input: {
  pagePath?: string;
  pageTitle?: string;
  widgetTitle?: string | null;
  focusEntityName?: string | null;
  hasQuotedSelectionPrefill?: boolean;
  overlayContext?: {
    active_modal?: string | null;
    active_modal_title?: string | null;
  } | null;
}): string {
  if (input.hasQuotedSelectionPrefill) {
    return 'Ask about your selected text…';
  }

  const pagePath = (input.pagePath ?? '').split('?')[0].trim().toLowerCase();
  if (pagePath.includes('/crm/my-quota') || pagePath.endsWith('/my-quota')) {
    return 'Ask about your quota, attainment, or what to prioritize…';
  }
  const fullPath = (input.pagePath ?? '').trim().toLowerCase();
  if (
    fullPath.includes('/settings') &&
    (fullPath.includes('tab=territories') || fullPath.includes('territories'))
  ) {
    return 'Ask about territory plans, quota alignment gaps, or rep allocation…';
  }
  if (
    fullPath.includes('/settings') &&
    (fullPath.includes('tab=sales-targets') || fullPath.includes('sales-targets'))
  ) {
    return 'Ask about team roll-ups, reps behind quota, or territory alignment…';
  }
  if (input.overlayContext?.active_modal === 'sales_target_set') {
    return 'Ask about quota amount, pipeline gap, or territory alignment…';
  }

  const widget = (input.widgetTitle ?? '').trim();
  if (widget) return `Ask about ${widget}…`;

  const focus = (input.focusEntityName ?? '').trim();
  if (pagePath.match(/^\/crm\/deals\/[0-9a-f-]{36}$/i)) {
    return focus
      ? 'Ask about playbook steps or next actions on this deal…'
      : 'Ask about this deal, playbook steps, or recommended next actions…';
  }
  if (focus) return `Ask about ${focus}…`;

  const pageTitle = (input.pageTitle ?? '').trim();
  const module = inferShellModuleFromPath(input.pagePath ?? '');

  switch (module) {
    case 'crm':
      return 'Ask about this deal, account, or your pipeline…';
    case 'messaging':
      return 'Ask about this conversation or thread…';
    case 'calendar':
      return 'Ask about your schedule or this event…';
    case 'pages':
      return 'Ask about this page or workspace…';
    case 'home':
      if (readDashboardSalesPersona() === 'manager') {
        return 'Ask about team accounts, at-risk deals, or quota coverage…';
      }
      return 'Ask about your dashboard or pipeline…';
    default:
      return pageTitle ? `Ask anything about ${pageTitle}…` : 'Ask anything about this page…';
  }
}

function moduleDefaultDockChips(
  module: string,
  focusEntityName?: string
): DockIdleStarterChip[] {
  switch (module) {
    case 'crm':
      return focusEntityName
        ? [
            {
              label: 'Run playbook for stage',
              prompt: `How should I run the next call for ${focusEntityName} using our sales playbook?`,
            },
            {
              label: 'Next step',
              prompt: `What's the next step on ${focusEntityName}?`,
            },
          ]
        : [
            {
              label: 'Pipeline summary',
              prompt: 'Show my pipeline summary and deals that need attention this week',
            },
          ];
    case 'messaging':
      return [
        {
          label: 'Summarize thread',
          prompt: 'Summarize this messaging thread and highlight action items',
        },
      ];
    case 'calendar':
      return [
        {
          label: 'Meeting prep',
          prompt: 'What should I prepare for my upcoming meetings?',
        },
      ];
    case 'pages':
      return [
        {
          label: 'Summarize page',
          prompt: 'Summarize this page and list the most important open items',
        },
      ];
    case 'home':
      return [
        {
          label: 'Pipeline focus',
          prompt: 'What deals need my attention this week?',
        },
      ];
    default:
      return [
        {
          label: 'What should I focus on?',
          prompt: 'What should I focus on right now?',
        },
      ];
  }
}

function truncateChipLabel(label: string): string {
  const trimmed = label.trim();
  return trimmed.length > 40 ? `${trimmed.slice(0, 37)}…` : trimmed;
}

function isDockIdleBrainAction(action: AssistantContextAction): boolean {
  return (
    isComposeDraftAction(action) ||
    isAdvisorExecuteAction(action) ||
    isPromptAssistantAction(action) ||
    isNavigateAssistantAction(action) ||
    isOpenEmailTemplateCreateAction(action) ||
    isOpenSalesTargetSetAction(action)
  );
}

function brainActionToIdleChip(action: AssistantContextAction): DockIdleStarterChip | null {
  const label = (action.label || '').trim();
  if (!label) return null;
  const mode = (action.execution_mode || 'prompt').toLowerCase();
  if (mode === 'open_email_template_create') {
    return { label: truncateChipLabel(label), prompt: label, contextAction: action };
  }
  if (mode === 'open_sales_target_set') {
    return { label: truncateChipLabel(label), prompt: label, contextAction: action };
  }
  if (isNavigateAssistantAction(action) || isComposeDraftAction(action)) {
    return { label: truncateChipLabel(label), prompt: label, contextAction: action };
  }
  if (mode !== 'prompt') return null;
  const prompt = (action.prompt_text || action.label || '').trim();
  if (!prompt) return null;
  return {
    label: truncateChipLabel(label),
    prompt,
    contextAction: action,
  };
}

/** Up to two compact starter chips above the dock composer (no full empty-state wall) [2.2.1351]. */
export function collectDockIdleStarterChips(input: {
  pagePath?: string;
  pageTitle?: string | null;
  widgetTitle?: string | null;
  focusEntityName?: string | null;
  brainContext?: AssistantContextResponse | null;
  maxChips?: number;
}): DockIdleStarterChip[] {
  const max = Math.min(3, Math.max(1, input.maxChips ?? 2));
  const seen = new Set<string>();
  const out: DockIdleStarterChip[] = [];

  const push = (chip: DockIdleStarterChip) => {
    const key = chip.label.trim().toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(chip);
  };

  const widget = (input.widgetTitle ?? '').trim();
  if (widget) {
    push({
      label: 'Explain chart',
      prompt: `Explain what ${widget} shows and what I should focus on`,
    });
    push({
      label: 'Key trends',
      prompt: `What trends or changes stand out in ${widget}?`,
    });
    return out.slice(0, max);
  }

  const focus = (input.focusEntityName ?? '').trim();

  const path = (input.pagePath ?? '').split('?')[0].trim().toLowerCase();
  const fullPath = (input.pagePath ?? '').trim().toLowerCase();

  if (path.match(/^\/crm\/deals\/[0-9a-f-]{36}$/i)) {
    const dealName = resolveDealFocusDisplayName(input, true);
    for (const action of input.brainContext?.suggested_next_actions ?? []) {
      if ((action.source || '') !== 'deal_focus_coaching') continue;
      const label = (action.label || '').trim();
      if (!label) continue;
      const prompt = dealFocusCoachingPromptForShell(label, action, dealName);
      if (!prompt) continue;
      push({ label, prompt });
      if (out.length >= max) return out.slice(0, max);
    }
    push({
      label: 'Run playbook for stage',
      prompt: `How should I run the next call for ${dealName} using our sales playbook?`,
    });
    push({
      label: 'Next step',
      prompt: `What's the next step on ${dealName}?`,
    });
    return out.slice(0, max);
  }

  if ((path === '/' || path === '/home') && readDashboardSalesPersona() === 'manager') {
    push({
      label: 'Team account risk',
      prompt: 'Which team accounts need attention first and who should act on each?',
    });
    push({
      label: 'Coaching plan',
      prompt: 'Draft a 3-bullet coaching plan for my reps based on this dashboard.',
    });
    return out.slice(0, max);
  }

  if (path.includes('/crm/my-quota') || path.endsWith('/my-quota')) {
    push({
      label: 'My quota status',
      prompt: 'How am I tracking against my quota this quarter?',
    });
    push({
      label: 'Set quota with AI',
      prompt: 'Help me set my sales quota for this quarter',
    });
    return out.slice(0, max);
  }

  if (
    path.includes('/settings') &&
    (fullPath.includes('tab=territories') || fullPath.includes('territories'))
  ) {
    push({
      label: 'Alignment gaps',
      prompt: 'Which territories have the largest quota alignment gap this quarter?',
    });
    push({
      label: 'Plan vs quotas',
      prompt: 'How do territory revenue plans compare to rep quotas on my team?',
    });
    return out.slice(0, max);
  }

  if (
    path.includes('/settings') &&
    (fullPath.includes('tab=sales-targets') || fullPath.includes('sales-targets'))
  ) {
    push({
      label: 'Team roll-up summary',
      prompt: 'Summarize my team quota roll-up: who is on track, at risk, and off track?',
    });
    push({
      label: 'Reps behind quota',
      prompt: 'Which reps on my team are behind quota and what coaching should I give?',
    });
    return out.slice(0, max);
  }

  for (const action of collectProactiveAdvisorContextActions(input.brainContext ?? null)) {
    const chip = brainActionToIdleChip(action);
    if (!chip) continue;
    push(chip);
    if (out.length >= max) return out;
  }

  for (const action of input.brainContext?.suggested_next_actions ?? []) {
    if (!isDockIdleBrainAction(action)) continue;
    const chip = brainActionToIdleChip(action);
    if (!chip) continue;
    push(chip);
    if (out.length >= max) return out;
  }

  const module = inferShellModuleFromPath(input.pagePath ?? '');
  for (const chip of moduleDefaultDockChips(module, focus || undefined)) {
    push(chip);
    if (out.length >= max) return out;
  }

  return out.slice(0, max);
}
